import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button, Form, Select, Input, Typography, Card, Flex } from '@app/ui';
import { createCampaign, getPostCampaigns } from '../api/postCampaigns';
import { getCampaignOptionsFromResponse } from '../utils/postCampaigns';
import connect from '@app/utils/connect';
import combineActions from '@app/utils/combineActions';
import sendNotification from '@app/actions/notifications/send';
import * as Sentry from '@sentry/browser';
import classNames from '@app/ui/sports-components/utils/classNames';
import '@app/components/common/modals/PostModal.scss';

const portalRoot = document.getElementById('assign-campaign-modal-root');

type AssignCampaignModalProps = {
    visible: boolean;
    clientId: number;
    onClose: () => void;
    handlePostAssignment: (campaignId: number, campaignName: string) => void;
    onAddingNewCampaignOption?: () => void;
    sendNotification: (
        message: string,
        config?: { context: 'success' | 'warning' | 'danger' },
    ) => void;
};

const AssignCampaignModal: React.FC<AssignCampaignModalProps> = ({
    visible,
    clientId,
    onClose,
    handlePostAssignment,
    onAddingNewCampaignOption,
    sendNotification,
}) => {
    const { t } = useTranslation('ownedSocial');
    const [form] = Form.useForm<{
        existingCampaign?: number[];
        newCampaign?: string;
    }>();
    const [campaigns, setCampaigns] = useState<
        Record<number, { name: string }>
    >({});
    const [loading, setLoading] = useState(true);

    // Load campaigns once
    useEffect(() => {
        (async () => {
            try {
                const data = await getPostCampaigns(clientId);
                setCampaigns(getCampaignOptionsFromResponse(data));
            } catch (err) {
                Sentry.captureException(err);
            } finally {
                setLoading(false);
            }
        })();
    }, [clientId]);

    const existingCampaign = Form.useWatch('existingCampaign', form);
    const newCampaign = Form.useWatch('newCampaign', form);

    const shouldCreateNewCampaign =
        (!existingCampaign || existingCampaign.length === 0) && !!newCampaign;

    const onFinish = async (values: {
        existingCampaign?: number[];
        newCampaign?: string;
    }) => {
        try {
            if (shouldCreateNewCampaign) {
                const created = await createCampaign(
                    clientId,
                    values.newCampaign,
                );
                onAddingNewCampaignOption?.();
                handlePostAssignment(created.id, values.newCampaign);
            } else if (
                values.existingCampaign &&
                values.existingCampaign.length > 0
            ) {
                // Handle multiple campaigns
                for (const campaignId of values.existingCampaign) {
                    const label = campaigns[campaignId]?.name || '';
                    handlePostAssignment(campaignId, label);
                }
            }
            onClose();
        } catch (err: any) {
            Sentry.captureException(err);
            sendNotification(err.message, { context: 'warning' });
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onClose();
    };

    if (!visible || !portalRoot) return null;

    const overlayClass = classNames('modal-overlay', {
        'modal-shown': visible,
    });

    return ReactDOM.createPortal(
        <div className={overlayClass} onClick={onClose} style={{ zIndex: 100 }}>
            <div
                className="modal-v2-wrapper"
                onClick={e => e.stopPropagation()}
            >
                <Card style={{ minWidth: 480 }}>
                    <i
                        onClick={onClose}
                        style={{
                            color: '#858585',
                            fontSize: 12,
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            cursor: 'pointer',
                        }}
                        className="fa-regular fa-x"
                    />
                    <Flex vertical>
                        <Flex
                            justify="space-between"
                            style={{ marginBottom: 12 }}
                            align="flex-start"
                        >
                            <Typography.Title
                                level={4}
                                className="assign-campaign-title"
                            >
                                {t(
                                    'socialPostMetrics.modal.assignPostToCampaign',
                                )}
                            </Typography.Title>
                        </Flex>

                        <div className="assign-campaign-body">
                            <Form
                                form={form}
                                layout="vertical"
                                onFinish={onFinish}
                                initialValues={{
                                    existingCampaign: [],
                                    newCampaign: '',
                                }}
                            >
                                <Form.Item
                                    name="existingCampaign"
                                    label={t(
                                        'socialPostMetrics.modal.selectCampaigns',
                                    )}
                                    dependencies={['newCampaign']}
                                    // disable when newCampaign has text
                                    rules={[]}
                                >
                                    <Select
                                        mode="multiple"
                                        placeholder={t(
                                            'socialPostMetrics.modal.selectCampaign',
                                        )}
                                        options={Object.entries(campaigns).map(
                                            ([key, { name }]) => ({
                                                label: name,
                                                value: Number(key),
                                            }),
                                        )}
                                        loading={loading}
                                        disabled={loading || !!newCampaign}
                                        allowClear
                                        optionFilterProp="label"
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="newCampaign"
                                    label={t(
                                        'socialPostMetrics.modal.createCampaign',
                                    )}
                                    dependencies={['existingCampaign']}
                                >
                                    <Input
                                        placeholder={t(
                                            'socialPostMetrics.modal.enterCampaignName',
                                        )}
                                        disabled={
                                            !!existingCampaign &&
                                            existingCampaign.length > 0
                                        }
                                        allowClear
                                    />
                                </Form.Item>

                                <Flex justify="flex-end" gap={12}>
                                    <Button
                                        onClick={handleCancel}
                                        type="primary"
                                        color="default"
                                        variant="solid"
                                    >
                                        {t('socialPostMetrics.cancel')}
                                    </Button>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        disabled={
                                            (!existingCampaign ||
                                                existingCampaign.length ===
                                                    0) &&
                                            !newCampaign
                                        }
                                    >
                                        {shouldCreateNewCampaign
                                            ? t(
                                                  'socialPostMetrics.modal.createAndAssign',
                                              )
                                            : t(
                                                  'socialPostMetrics.modal.assign',
                                              )}
                                    </Button>
                                </Flex>
                            </Form>
                        </div>
                    </Flex>
                </Card>
            </div>
        </div>,
        portalRoot,
    );
};

export default connect(
    ['client'],
    combineActions({ sendNotification }),
)(AssignCampaignModal);
